import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Mail, Phone, MapPin, CheckCircle2, Loader2, AlertTriangle, ArrowRight, Linkedin } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SCOPES = [
  "Contract Project",
  "Consulting / Advisory",
  "Interim / Fractional Role",
  "Full-Time Opportunity",
  "Other",
];

const EMPTY = { name: "", company: "", email: "", scope: "", message: "" };

export default function LeadForm() {
  const [form, setForm] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState("idle");
  const [result, setResult] = useState(null);

  const set = (k) => (e) => {
    setForm((f) => ({ ...f, [k]: e.target.value }));
    setErrors((er) => ({ ...er, [k]: undefined }));
  };

  const validate = () => {
    const er = {};
    if (!form.name.trim()) er.name = "Your name is required.";
    if (!form.email.trim()) er.email = "Email is required.";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) er.email = "Enter a valid email address.";
    if (!form.scope) er.scope = "Select an engagement type.";
    if (!form.message.trim()) er.message = "Tell me a little about the project or role.";
    return er;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    const er = validate();
    if (Object.keys(er).length) {
      setErrors(er);
      return;
    }
    setStatus("submitting");
    try {
      const res = await fetch(`${API}/leads`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error("Request failed");
      const data = await res.json();
      setResult(data);
      setStatus("success");
    } catch {
      setStatus("error");
    }
  };

  const reset = () => {
    setForm(EMPTY);
    setErrors({});
    setResult(null);
    setStatus("idle");
  };

  const inputCls = (er) =>
    `w-full rounded-xl bg-[#0B0F17]/80 border px-4 py-3.5 text-[15px] text-white placeholder-[#6B7280] outline-none transition-all duration-300 focus:border-[#00F0FF]/70 focus:ring-2 focus:ring-[#00F0FF]/20 ${
      er ? "border-[#F59E0B]/60" : "border-white/[0.09] hover:border-white/20"
    }`;

  return (
    <section id="contact" className="relative py-24 sm:py-32 bg-[#0D1320]/60 overflow-hidden">
      <div
        className="absolute -bottom-40 -left-32 w-[520px] h-[520px] rounded-full bg-[#2563EB]/10 blur-[140px] pointer-events-none"
        aria-hidden="true"
      />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 26 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.7 }}
          className="max-w-3xl"
        >
          <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-[#00F0FF]">
            06 — Start the Conversation
          </p>
          <h2 className="mt-4 font-display font-extrabold tracking-tight text-3xl sm:text-4xl lg:text-5xl text-white leading-[1.1]">
            Ready to trade spreadsheet chaos for MRP discipline?
          </h2>
          <p className="mt-5 text-base sm:text-lg text-[#9CA3AF] leading-relaxed">
            Project inquiries, contract roles, or full-time opportunities — your
            inquiry lands directly in my inbox, and every submission is logged so
            nothing slips through.
          </p>
        </motion.div>

        <div className="mt-14 grid lg:grid-cols-5 gap-8 lg:gap-12">
          <motion.aside
            initial={{ opacity: 0, x: -28 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.7 }}
            className="lg:col-span-2"
          >
            <div className="rounded-2xl border border-white/[0.07] bg-[#111827]/70 p-7 sm:p-8 h-full">
              <h3 className="font-mono text-[11px] uppercase tracking-[0.26em] text-[#00F0FF]">
                Direct Lines
              </h3>
              <ul className="mt-6 space-y-5">
                <li>
                  <a
                    href="mailto:robdan316@gmail.com"
                    data-testid="contact-email-link"
                    className="flex items-center gap-4 group"
                  >
                    <span className="h-10 w-10 shrink-0 rounded-lg border border-[#00F0FF]/25 bg-[#00F0FF]/[0.07] text-[#00F0FF] flex items-center justify-center">
                      <Mail size={16} />
                    </span>
                    <span>
                      <span className="block text-[11px] font-mono uppercase tracking-[0.18em] text-[#6B7280]">Email</span>
                      <span className="text-[15px] text-[#D1D5DB] group-hover:text-[#00F0FF] transition-colors">robdan316@gmail.com</span>
                    </span>
                  </a>
                </li>
                <li>
                  <a href="tel:+18582317380" data-testid="contact-phone-link" className="flex items-center gap-4 group">
                    <span className="h-10 w-10 shrink-0 rounded-lg border border-[#00F0FF]/25 bg-[#00F0FF]/[0.07] text-[#00F0FF] flex items-center justify-center">
                      <Phone size={16} />
                    </span>
                    <span>
                      <span className="block text-[11px] font-mono uppercase tracking-[0.18em] text-[#6B7280]">Phone</span>
                      <span className="text-[15px] text-[#D1D5DB] group-hover:text-[#00F0FF] transition-colors">(858) 231-7380</span>
                    </span>
                  </a>
                </li>
                <li>
                  <a
                    href="https://www.linkedin.com/in/robert-w-daniels/"
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="contact-linkedin-link"
                    className="flex items-center gap-4 group"
                  >
                    <span className="h-10 w-10 shrink-0 rounded-lg border border-[#00F0FF]/25 bg-[#00F0FF]/[0.07] text-[#00F0FF] flex items-center justify-center">
                      <Linkedin size={16} />
                    </span>
                    <span>
                      <span className="block text-[11px] font-mono uppercase tracking-[0.18em] text-[#6B7280]">LinkedIn</span>
                      <span className="text-[15px] text-[#D1D5DB] group-hover:text-[#00F0FF] transition-colors">robert-w-daniels</span>
                    </span>
                  </a>
                </li>
                <li className="flex items-center gap-4">
                  <span className="h-10 w-10 shrink-0 rounded-lg border border-[#00F0FF]/25 bg-[#00F0FF]/[0.07] text-[#00F0FF] flex items-center justify-center">
                    <MapPin size={16} />
                  </span>
                  <span>
                    <span className="block text-[11px] font-mono uppercase tracking-[0.18em] text-[#6B7280]">Location</span>
                    <span className="text-[15px] text-[#D1D5DB]">Poway, CA</span>
                  </span>
                </li>
              </ul>
              <div className="mt-8 pt-7 border-t border-white/[0.07]">
                <h4 className="font-mono text-[11px] uppercase tracking-[0.26em] text-[#00F0FF]">Open To</h4>
                <div className="mt-4 flex flex-wrap gap-2">
                  {["Contract Projects", "Interim Roles", "Full-Time Opportunities", "Consulting"].map((t) => (
                    <span key={t} className="text-[11px] text-[#D1D5DB] bg-white/[0.05] border border-white/[0.08] rounded-full px-3 py-1.5">
                      {t}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </motion.aside>

          <motion.div
            initial={{ opacity: 0, x: 28 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="lg:col-span-3"
          >
            <div className="rounded-2xl border border-white/[0.07] bg-[#111827]/70 p-7 sm:p-9">
              <AnimatePresence mode="wait">
                {status === "success" && result ? (
                  <motion.div
                    key="success"
                    data-testid="lead-success-panel"
                    initial={{ opacity: 0, scale: 0.96 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0 }}
                    className="text-center py-10"
                  >
                    <span className="mx-auto h-16 w-16 rounded-full border border-[#10B981]/40 bg-[#10B981]/10 text-[#10B981] flex items-center justify-center">
                      <CheckCircle2 size={30} />
                    </span>
                    <h3 className="mt-6 font-display font-bold text-2xl text-white">Inquiry received.</h3>
                    <p className="mt-3 text-[#9CA3AF] max-w-sm mx-auto leading-relaxed text-sm sm:text-base">
                      {result.email_sent
                        ? "It's saved and a notification just landed in my inbox — expect a straightforward, no-fluff reply."
                        : "It's saved and queued for review — expect a straightforward, no-fluff reply."}
                    </p>
                    <p className="mt-5 inline-block font-mono text-[11px] uppercase tracking-[0.2em] text-[#00F0FF] border border-[#00F0FF]/25 bg-[#00F0FF]/[0.05] rounded-full px-4 py-2">
                      Ref · {result.ticket}
                    </p>
                    <button
                      onClick={reset}
                      data-testid="lead-send-another-button"
                      className="mt-8 block mx-auto font-display font-semibold text-[#F9FAFB] border border-white/20 hover:border-[#00F0FF]/70 hover:bg-white/5 rounded-full px-6 py-3 transition-all duration-300"
                    >
                      Send Another Inquiry
                    </button>
                  </motion.div>
                ) : (
                  <motion.form
                    key="form"
                    data-testid="lead-form"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    onSubmit={onSubmit}
                    noValidate
                    className="space-y-5"
                  >
                    <div className="grid sm:grid-cols-2 gap-5">
                      <div>
                        <label htmlFor="lead-name" className="block font-mono text-[10px] uppercase tracking-[0.2em] text-[#9CA3AF] mb-2">
                          Name *
                        </label>
                        <input
                          id="lead-name"
                          data-testid="lead-name-input"
                          value={form.name}
                          onChange={set("name")}
                          placeholder="Jane Manufactura"
                          className={inputCls(errors.name)}
                        />
                        {errors.name && <p data-testid="lead-name-error" className="mt-1.5 text-xs text-[#F59E0B]">{errors.name}</p>}
                      </div>
                      <div>
                        <label htmlFor="lead-company" className="block font-mono text-[10px] uppercase tracking-[0.2em] text-[#9CA3AF] mb-2">
                          Company
                        </label>
                        <input
                          id="lead-company"
                          data-testid="lead-company-input"
                          value={form.company}
                          onChange={set("company")}
                          placeholder="Acme Manufacturing Co."
                          className={inputCls(errors.company)}
                        />
                      </div>
                    </div>

                    <div className="grid sm:grid-cols-2 gap-5">
                      <div>
                        <label htmlFor="lead-email" className="block font-mono text-[10px] uppercase tracking-[0.2em] text-[#9CA3AF] mb-2">
                          Email *
                        </label>
                        <input
                          id="lead-email"
                          type="email"
                          data-testid="lead-email-input"
                          value={form.email}
                          onChange={set("email")}
                          placeholder="jane@acmemfg.com"
                          className={inputCls(errors.email)}
                        />
                        {errors.email && <p data-testid="lead-email-error" className="mt-1.5 text-xs text-[#F59E0B]">{errors.email}</p>}
                      </div>
                      <div>
                        <label htmlFor="lead-scope" className="block font-mono text-[10px] uppercase tracking-[0.2em] text-[#9CA3AF] mb-2">
                          Project / Role Scope *
                        </label>
                        <select
                          id="lead-scope"
                          data-testid="lead-scope-select"
                          value={form.scope}
                          onChange={set("scope")}
                          className={`${inputCls(errors.scope)} appearance-none ${form.scope ? "" : "text-[#6B7280]"}`}
                        >
                          <option value="" disabled>
                            Select engagement type
                          </option>
                          {SCOPES.map((s) => (
                            <option key={s} value={s} className="bg-[#111827] text-white">
                              {s}
                            </option>
                          ))}
                        </select>
                        {errors.scope && <p data-testid="lead-scope-error" className="mt-1.5 text-xs text-[#F59E0B]">{errors.scope}</p>}
                      </div>
                    </div>

                    <div>
                      <label htmlFor="lead-message" className="block font-mono text-[10px] uppercase tracking-[0.2em] text-[#9CA3AF] mb-2">
                        Message *
                      </label>
                      <textarea
                        id="lead-message"
                        data-testid="lead-message-input"
                        value={form.message}
                        onChange={set("message")}
                        rows={5}
                        placeholder="Where are the spreadsheets hurting? What does your current BOM and inventory workflow look like?"
                        className={`${inputCls(errors.message)} resize-none`}
                      />
                      {errors.message && <p data-testid="lead-message-error" className="mt-1.5 text-xs text-[#F59E0B]">{errors.message}</p>}
                    </div>

                    {status === "error" && (
                      <div data-testid="lead-error-alert" className="flex items-center gap-3 rounded-xl border border-[#F59E0B]/40 bg-[#F59E0B]/[0.07] px-4 py-3 text-sm text-[#F59E0B]">
                        <AlertTriangle size={16} />
                        Something went wrong submitting your inquiry — please try again.
                      </div>
                    )}

                    <motion.button
                      type="submit"
                      data-testid="lead-submit-button"
                      disabled={status === "submitting"}
                      whileHover={{ y: -2 }}
                      whileTap={{ scale: 0.98 }}
                      className="w-full sm:w-auto inline-flex items-center justify-center gap-2.5 font-display font-semibold text-[#0B0F17] bg-[#00F0FF] hover:bg-[#4DF7FF] disabled:opacity-60 disabled:cursor-not-allowed rounded-full px-8 py-4 shadow-[0_10px_40px_rgba(0,240,255,0.22)] transition-colors duration-300"
                    >
                      {status === "submitting" ? (
                        <>
                          <Loader2 size={17} className="animate-spin" />
                          Submitting…
                        </>
                      ) : (
                        <>
                          Send Inquiry
                          <ArrowRight size={17} />
                        </>
                      )}
                    </motion.button>
                  </motion.form>
                )}
              </AnimatePresence>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
